/**
 * @fileoverview The client's copy of the tier catalogue (X9).
 *
 * Every surface that names a plan reads it from here. Before this module the
 * name was computed inline, and always the same way:
 *
 *     tier === "concertmaster" ? "Studio" : "Pro"
 *
 * — in eight separate places. That expression was fine when there were exactly
 * two paid tiers, and the restructure made it wrong three ways at once. It now
 * calls the TOP tier by the name of the cheaper AI-free one; it labels `basic`
 * and the new `studio` tier "Pro", so a free account and a $19 account both
 * read as a plan neither is on; and because it can only answer with one of two
 * names, adding a tier could never fix it — a fourth plan had nowhere to go.
 *
 * So the point of the module is not tidiness. It is that a rename is a data
 * edit rather than a search, which is the whole reason X9 was expensive enough
 * to be its own item.
 *
 * ## What is mirrored, and from where
 *
 * `backend/tier_config.py` is authoritative for all of it — the display names,
 * the capability ranking, the three tier axes, and the price ladder. The
 * server decides what an account may do; this is only how we NAME it. Keep the
 * two in step (there is a test per table below asserting the shape we expect),
 * and when they disagree, the backend is right.
 *
 * Mirrored again in `mobile/src/utils/tiers.js`, which runs the same fixture
 * table — the precedent set by `pricing.js` and `usageMeter.js`. A plan that
 * reads "Studio AI" on a laptop and "Studio" on a phone is the same defect
 * this module exists to remove, just spread across two devices.
 */

/**
 * Plan names as a customer sees them.
 *
 * Internal keys never change — Stripe price ids, Apple product ids and
 * `PRICE_TIER_MAP` all key on them, and `concertmaster` has now displayed as
 * "Concertmaster", "Studio" and "Studio AI" while the key stood still. That is
 * exactly why the id is not allowed to spell the name.
 *
 * `pro` is LEGACY: still charged, still renewed for the grandfathering window,
 * never sold. It keeps its name because subscribers still see it.
 */
export const TIER_DISPLAY_NAMES = {
  basic: "Solo",
  studio: "Studio",
  pro: "Pro",
  concertmaster: "Studio AI",
};

/**
 * Capability ranking — what a plan can DO, not what it costs. `studio` and the
 * legacy `pro` are both $19 and `pro` outranks it because it includes AI.
 *
 * Mirrors `tier_config.TIER_RANK`. A tier missing here does not rank low, it
 * ranks as free, which silently erases a paid entitlement — the bug X7 found
 * in the backend's own copy, where `studio` had been missing since X2.
 */
export const TIER_RANK = { basic: 0, studio: 1, pro: 2, concertmaster: 3 };

/** Tiers above free. Mirrors `tier_config.PAID_TIERS`. */
export const PAID_TIERS = ["studio", "pro", "concertmaster"];

/**
 * Tiers with the premium AI set — group-lesson AI, practice-review AI, and
 * repertoire personalization. Mirrors `tier_config.AI_FEATURE_TIERS`.
 *
 * Deliberately NOT the same question as "is this plan paid": `studio` is paid
 * and has no AI at all, while free records lessons on a one-time grant. Asking
 * the wrong one is how a gate takes AI away from every free instructor.
 */
export const AI_FEATURE_TIERS = ["pro", "concertmaster"];

/**
 * Tiers that record lessons for AI summaries at all. Mirrors
 * `tier_config.LESSON_AI_TIERS`.
 *
 * Strictly WIDER than the premium set above, and `basic` belongs here: the
 * free tier has always recorded, just on a small one-time grant. It is the
 * right question for "can this account use recording minutes" — which is what
 * decides whether offering a minute top-up makes any sense (I6). Asking
 * `hasAiFeatures` instead would refuse to sell minutes to every free
 * instructor; asking `isPaidTier` would offer them to `studio`, which is paid
 * and deliberately records nothing.
 */
export const LESSON_AI_TIERS = ["basic", "pro", "concertmaster"];

/** Tiers that can actually be bought today, cheapest first. */
export const OFFERED_TIERS = ["studio", "concertmaster"];

/**
 * Monthly list price in cents. Mirrors `tier_config.MONTHLY_PRICE_CENTS`.
 *
 * Legacy `pro` is absent for the same reason it is absent server-side: it is
 * charged but not sold, and its retired annual price predates the
 * ten-for-twelve convention, so quoting a derived figure for it would state a
 * price no Stripe price has. Callers get undefined rather than a guess.
 */
export const TIER_MONTHLY_CENTS = {
  basic: 0,
  studio: 1900,
  concertmaster: 3900,
};

/**
 * How many ACTIVE students each plan's roster holds; `null` is unlimited.
 * Mirrors `tier_config.TIER_LIMITS[tier].max_students`.
 *
 * "Active" is the archive STATE (I2) — who is on the working roster right now.
 * An archived student is kept in full and costs nothing, which is what makes
 * archiving the way back under the cap rather than a consolation prize.
 *
 * Only free is capped; no plan a customer can buy meters their roster.
 */
export const TIER_MAX_STUDENTS = {
  basic: 5,
  studio: null,
  pro: null,
  concertmaster: null,
};

/**
 * Minutes granted ONCE at signup, per tier. Mirrors
 * `tier_config.TIER_LIMITS[tier].signup_allowance_minutes` (I3).
 *
 * Only free grants any: 180 minutes to try the product, once. A paid plan's
 * minutes are recurring, so it has nothing to grant here. Declared so the
 * plan descriptions below can quote the figure rather than type it.
 */
export const TIER_SIGNUP_MINUTES = {
  basic: 180,
  studio: 0,
  pro: 0,
  concertmaster: 0,
};

/**
 * What each plan's recording allowance IS, in the fewest words that stay true.
 *
 * Three of these are a zero and they mean different things, which is the whole
 * reason this is a sentence rather than a number: free has no recurring
 * allowance because its minutes are granted once, and `studio` has none
 * because it deliberately includes no AI. "0 minutes" would describe both as
 * having run out.
 */
export const TIER_ALLOWANCE_LABEL = {
  basic: "180 AI minutes, one time",
  studio: "No AI recording",
  pro: "40 hrs of AI recording a month",
  concertmaster: "50 hrs of AI recording a month",
};

/** One line on what the plan is for. Shared so every upsell says the same thing. */
export const TIER_TAGLINE = {
  basic: "Keep a roster and write lesson notes",
  studio: "The whole studio platform, notes in your own words",
  pro: "AI lesson summaries and the full platform",
  concertmaster: "Hans at full strength",
};

/**
 * What each plan INCLUDES, as the bullets every surface that describes a plan
 * renders — the public pricing page and the upgrade dialog on Settings.
 *
 * One table, deliberately. The two surfaces used to carry their own lists
 * (six bullets on the pricing page, three on the tiles), so the same plan was
 * described two ways depending on which door somebody came in by, and the
 * upgrade dialog quietly omitted things the pricing page had promised. A plan
 * is one promise; it gets one description.
 *
 * Figures and names are pulled from the tables above rather than typed, so a
 * repricing or a rename moves the bullets on its own. Legacy `pro` has no
 * entry because it is never described to anyone: it cannot be bought.
 */
export const TIER_FEATURES = {
  basic: [
    `Up to ${TIER_MAX_STUDENTS.basic} active students`,
    "Student roster, lesson history & unlimited notes",
    `${TIER_SIGNUP_MINUTES.basic} minutes of AI lesson recording, one time`,
    "AI summaries emailed to families",
    "Metronome & tuner",
  ],
  studio: [
    `Everything in ${TIER_DISPLAY_NAMES.basic}, unlimited students`,
    "Full scheduling & reminders",
    "Booking page with every lesson format",
    "Student & parent portal",
    "Google Calendar sync through Scheduler",
  ],
  concertmaster: [
    `Everything in ${TIER_DISPLAY_NAMES.studio}`,
    `${TIER_ALLOWANCE_LABEL.concertmaster} (≈100 half-hour lessons)`,
    "AI summaries, practice items & student focus",
    "Group & ensemble lesson AI",
    "Repertoire guides tailored to each student",
  ],
};

/**
 * What a plan deliberately does NOT include, where that needs saying.
 *
 * A checkmark list can only say what is present. `studio` is the first paid
 * plan in Hans's history with no AI, sitting above a FREE plan that records —
 * so the natural assumption about a $19 upgrade is exactly wrong, and someone
 * paying $19 expecting AI summaries is a refund and a bad review. The absence
 * gets its own line, on every surface that describes the plan.
 */
export const TIER_NOTE = {
  studio: `${TIER_ALLOWANCE_LABEL.studio} — ${TIER_DISPLAY_NAMES.studio} is the whole platform for teachers who write their own lesson notes.`,
};

const titleCase = (value) =>
  value
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * The customer-facing name of a tier.
 *
 * No tier at all is Solo — an account without a subscription is on the free
 * plan, and that is the one substitution that is always safe. An UNRECOGNISED
 * tier is title-cased instead, never folded into Solo: a key we do not know is
 * a backend ahead of this build, and telling a paying instructor they are on
 * the free plan is the failure that matters. Mirrors the backend's
 * `tier_display_name`, which falls back the same way.
 *
 * @param {string|null|undefined} tier
 * @returns {string}
 */
export function tierDisplayName(tier) {
  if (!tier) return TIER_DISPLAY_NAMES.basic;
  return TIER_DISPLAY_NAMES[tier] || titleCase(tier);
}

/**
 * Capability rank; an unknown tier ranks as free.
 *
 * @param {string|null|undefined} tier
 * @returns {number}
 */
export function tierRank(tier) {
  return TIER_RANK[tier] ?? 0;
}

/**
 * Whether this tier is above free — i.e. whether the account is paying.
 *
 * What "Active" means beside a plan name, and what decides whether we offer an
 * Upgrade button. Not an AI question: `studio` is paid and AI-free.
 *
 * @param {string|null|undefined} tier
 * @returns {boolean}
 */
export function isPaidTier(tier) {
  return PAID_TIERS.includes(tier);
}

/**
 * Whether this tier includes the premium AI set.
 *
 * @param {string|null|undefined} tier
 * @returns {boolean}
 */
export function hasAiFeatures(tier) {
  return AI_FEATURE_TIERS.includes(tier);
}

/**
 * Whether a tier records lessons for AI summaries at all — and so whether
 * recording minutes mean anything to it.
 *
 * @param {string} tier
 * @returns {boolean}
 */
export function hasLessonAi(tier) {
  return LESSON_AI_TIERS.includes(tier);
}

/**
 * How many active students this plan holds, or `null` for unlimited.
 *
 * **An unrecognised tier answers "unlimited", which is the OPPOSITE of the
 * server's fallback, and both are right.** The backend is enforcing, so an
 * unknown tier there must get the restrictive answer or a typo becomes a
 * giveaway. This is describing, and a client that has not shipped yet meeting
 * a tier the backend already knows about would otherwise tell a paying
 * instructor their roster is capped at five — the same failure
 * `tierDisplayName` refuses when it title-cases rather than saying "Solo".
 *
 * Prefer `usage.max_students` from `/subscription/usage` when you have it: the
 * server resolves it org-aware, so a seated instructor reads as unlimited even
 * though their personal tier is free. This table is for the places that
 * describe a plan nobody is on yet — a paywall tile, a comparison row.
 *
 * @param {string|null|undefined} tier
 * @returns {number|null} The cap, or null when the roster is unlimited.
 */
export function maxActiveStudents(tier) {
  if (!(tier in TIER_MAX_STUDENTS)) return null;
  return TIER_MAX_STUDENTS[tier];
}

/**
 * Monthly list price in cents, or null when the tier is not sold.
 *
 * @param {string|null|undefined} tier
 * @returns {number|null}
 */
export function tierMonthlyCents(tier) {
  const cents = TIER_MONTHLY_CENTS[tier];
  return typeof cents === "number" ? cents : null;
}

/**
 * The highest-ranked tier among some candidates, e.g. several org seats.
 *
 * @param {Array<string|null|undefined>} tiers
 * @returns {string|null} The winning tier, or null if there were none.
 */
export function highestTier(tiers) {
  let best = null;
  for (const tier of tiers || []) {
    if (!tier) continue;
    if (best === null || tierRank(tier) > tierRank(best)) best = tier;
  }
  return best;
}

/**
 * The tier an account is actually ENTITLED to, as the server resolved it.
 *
 * `/auth/me` carries `effective_tier`: the stored subscription tier with the
 * grandfathering / founding pin applied and any paying org membership folded
 * in. Reading `subscription_tier` instead is how Settings came to tell a
 * Founding Ensemble member they were on Solo, with an Upgrade button, while
 * the badge beside their name said Studio AI — the stored tier IS free for
 * them; the pin is what makes it not. An older payload without the field
 * falls back to the stored tier, which is right for every account that has
 * no pin.
 *
 * @param {object|null|undefined} user The instructor from /auth/me.
 * @returns {string} A tier key; `basic` when nothing is known.
 */
export function effectiveTier(user) {
  return user?.effective_tier || user?.subscription_tier || "basic";
}

/**
 * The highest-ranked org seat covering this account, from the per-membership
 * seats on `/auth/me` (Spec 12), or null. Ranks through the shared table
 * rather than a local map — a local `{pro: 1, concertmaster: 2}` had never
 * been told about `studio`, so a seat on it ranked below nothing at all.
 *
 * @param {object|null|undefined} user
 * @returns {{tier: string, org_name?: string}|null}
 */
export function bestOrgSeat(user) {
  let best = null;
  for (const m of user?.org_memberships || []) {
    if (m.seat && (!best || tierRank(m.seat.tier) > tierRank(best.tier))) best = m.seat;
  }
  return best;
}

/**
 * The tier to NAME for an account from `/auth/me` alone — before, or without,
 * the status endpoint's answer. Mirrors that endpoint's seat rule: a seat
 * names the plan only when the personal tier is free; otherwise the account
 * is paying for its own, and the seat is reported beside it.
 *
 * @param {object|null|undefined} user
 * @returns {string}
 */
export function accountTier(user) {
  const local = effectiveTier(user);
  const seat = bestOrgSeat(user);
  return seat && !isPaidTier(local) ? seat.tier : local;
}

/**
 * The cheapest plan a buyer can ACTUALLY buy that includes the premium AI
 * features — practice review, group-lesson AI, repertoire personalisation.
 *
 * Derived rather than declared, because the obvious literal is wrong: the
 * cheapest tier in `AI_FEATURE_TIERS` is `pro`, which has not been sellable
 * since the restructure, so upgrade copy naming it pointed instructors at a
 * plan with no checkout behind it. Intersecting with `OFFERED_TIERS` is what
 * keeps that from happening again when the ladder next moves.
 *
 * @returns {string|null} The tier key, or null if nothing on sale has them.
 */
export function cheapestAiFeatureTier() {
  return (
    OFFERED_TIERS.filter((t) => AI_FEATURE_TIERS.includes(t)).sort(
      (a, b) => tierRank(a) - tierRank(b),
    )[0] || null
  );
}
